import { PROPOSAL_ACTIONS } from "@repo/core/constants/proposals";
import { Permission } from "@repo/core/constants/permissions";
import {
  PROPOSTA_SITUACAO,
  ProposalTypeEnum,
  ProposalTypeValue
} from "@repo/core/services/entities/Proposals";
import { ProposalOptionsItemProps } from "@repo/core/screens/Proposals/screens/ProposalsSearch/components/ProposalOptionsSheet/ProposalOptionsItem/types";
import { filterEnabledProposalActions } from "@repo/core/utils/proposalFeatureFlags";
import type { ProposalFeatureFlags } from "@repo/core/utils/proposalFeatureFlags";

export type AnalysisQueueProposalMenuOption = Omit<
  ProposalOptionsItemProps,
  "onPress"
>;

type ProposalActionRule = AnalysisQueueProposalMenuOption & {
  appliesTo?: ProposalTypeEnum[];
};

type ProposalActionPermission = Permission | string;

interface AnalysisQueueRule {
  visibleInQueue: boolean;
  actions: ProposalActionRule[];
}

const seeProposalAction: ProposalActionRule = {
  label: "Ver proposta",
  icon: "eye",
  action: PROPOSAL_ACTIONS.SEE_PROPOSAL
};

const editProposalAction: ProposalActionRule = {
  label: "Editar proposta",
  icon: "pen-square",
  action: PROPOSAL_ACTIONS.EDIT_PROPOSAL
};

const editPreProposalAction: ProposalActionRule = {
  label: "Editar pré-proposta",
  icon: "pen-square",
  action: PROPOSAL_ACTIONS.EDIT_PRE_PROPOSAL
};

const effectiveCreditAction: ProposalActionRule = {
  label: "Efetivar crédito",
  icon: "verified",
  action: PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL
};

const proceedAnalysisAction: ProposalActionRule = {
  label: "Prosseguir análise",
  icon: "pen-square",
  action: PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL
};

const effectiveFidelityAction: ProposalActionRule = {
  label: "Efetivar fidelidade",
  icon: "verified",
  action: PROPOSAL_ACTIONS.FIDELITY_EFFECTIVE
};

const sendToCreditTableAction: ProposalActionRule = {
  label: "Enviar para mesa de crédito",
  icon: "hand-holding-dollar",
  action: PROPOSAL_ACTIONS.SEND_TO_CREDIT_TABLE
};

const withPermission = (
  action: ProposalActionRule,
  permission: Permission
): ProposalActionRule => ({
  ...action,
  permissions: [permission]
});

const seeAnalysisQueueAction = withPermission(
  seeProposalAction,
  Permission.CRT_WEB_ATE_CON_FILA_ANALISE
);

const editAnalysisQueueAction = withPermission(
  editProposalAction,
  Permission.CRT_WEB_ATE_CON_FILA_ANALISE_EDT
);

const effectiveAnalysisQueueAction = withPermission(
  effectiveCreditAction,
  Permission.CRT_WEB_ATE_CON_FILA_ANALISE_EFE
);

const effectiveFidelityAnalysisQueueAction = withPermission(
  effectiveFidelityAction,
  Permission.CRT_WEB_ATE_CON_FILA_ANALISE_EFE
);

const sendToCreditTableAnalysisQueueAction = withPermission(
  sendToCreditTableAction,
  Permission.CRT_WEB_ATE_CON_FILA_ANALISE_MESA
);

export const analysisQueueRules: Record<PROPOSTA_SITUACAO, AnalysisQueueRule> =
  {
    [PROPOSTA_SITUACAO.QUEUE_ANALYSIS_AUTOMATIC]: {
      visibleInQueue: true,
      actions: []
    },
    [PROPOSTA_SITUACAO.WAITING_FOR_ANALYSIS]: {
      visibleInQueue: true,
      actions: []
    },
    [PROPOSTA_SITUACAO.ANALYSIS_AUTOMATIC]: {
      visibleInQueue: true,
      actions: []
    },
    [PROPOSTA_SITUACAO.ANALYSIS_MANUAL]: {
      visibleInQueue: true,
      actions: [seeProposalAction]
    },
    [PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY]: {
      visibleInQueue: true,
      actions: [
        {
          ...seeAnalysisQueueAction,
          appliesTo: [ProposalTypeEnum.AMBOS]
        },
        { ...editAnalysisQueueAction, appliesTo: [ProposalTypeEnum.AMBOS] },
        {
          ...effectiveAnalysisQueueAction,
          appliesTo: [ProposalTypeEnum.AMBOS]
        },
        {
          ...sendToCreditTableAnalysisQueueAction,
          appliesTo: [ProposalTypeEnum.AMBOS]
        },
        {
          ...effectiveFidelityAnalysisQueueAction,
          appliesTo: [ProposalTypeEnum.FIDELITY]
        }
      ]
    },
    [PROPOSTA_SITUACAO.APPROVED_MANUALLY]: {
      visibleInQueue: true,
      actions: [
        seeAnalysisQueueAction,
        editAnalysisQueueAction,
        effectiveAnalysisQueueAction,
        sendToCreditTableAnalysisQueueAction
      ]
    },
    [PROPOSTA_SITUACAO.REJECTED_AUTOMATICALLY]: {
      visibleInQueue: true,
      actions: [
        seeAnalysisQueueAction,
        editAnalysisQueueAction,
        effectiveFidelityAnalysisQueueAction,
        sendToCreditTableAnalysisQueueAction
      ]
    },
    [PROPOSTA_SITUACAO.REJECTED_MANUALLY]: {
      visibleInQueue: true,
      actions: [
        seeAnalysisQueueAction,
        editProposalAction,
        effectiveFidelityAnalysisQueueAction,
        sendToCreditTableAnalysisQueueAction
      ]
    },
    [PROPOSTA_SITUACAO.PENDING_CORRECTION]: {
      visibleInQueue: true,
      actions: [seeProposalAction, editProposalAction, sendToCreditTableAction]
    },
    [PROPOSTA_SITUACAO.PENDING_ANALYSIS]: {
      visibleInQueue: true,
      actions: [seeProposalAction, editProposalAction, sendToCreditTableAction]
    },
    [PROPOSTA_SITUACAO.EFFECTIVE]: {
      visibleInQueue: true,
      actions: [seeProposalAction]
    },
    [PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE]: {
      visibleInQueue: true,
      actions: [seeProposalAction]
    },
    [PROPOSTA_SITUACAO.ERROR_ANALYSIS]: {
      visibleInQueue: true,
      actions: [seeProposalAction, editProposalAction, sendToCreditTableAction]
    },
    [PROPOSTA_SITUACAO.CANCELLED]: {
      visibleInQueue: false,
      actions: []
    },
    [PROPOSTA_SITUACAO.PRE_PROPOSAL]: {
      visibleInQueue: true,
      actions: []
    },
    [PROPOSTA_SITUACAO.PRE_PROPOSAL_APPROVED]: {
      visibleInQueue: true,
      actions: [proceedAnalysisAction]
    },
    [PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED]: {
      visibleInQueue: true,
      actions: [
        seeProposalAction,
        editPreProposalAction,
        effectiveFidelityAnalysisQueueAction,
        sendToCreditTableAnalysisQueueAction
      ]
    },
    [PROPOSTA_SITUACAO.PRE_PROPOSAL_ANALYSIS]: {
      visibleInQueue: true,
      actions: []
    },
    [PROPOSTA_SITUACAO.DOCUMENT_CONFERENCE]: {
      visibleInQueue: false,
      actions: []
    },
    [PROPOSTA_SITUACAO.DATA_CONFERENCE]: {
      visibleInQueue: false,
      actions: []
    }
  };

export const normalizeProposalSituationCode = (
  statusCode: string | number | undefined | null
): string => {
  if (statusCode === undefined || statusCode === null) {
    return "";
  }

  const trimmed = String(statusCode).trim();
  if (trimmed === "") {
    return "";
  }

  return trimmed.length === 1 ? `0${trimmed}` : trimmed;
};

export const getAnalysisQueueRule = (
  statusCode: string | undefined
): AnalysisQueueRule | undefined => {
  const normalized = normalizeProposalSituationCode(statusCode);
  if (
    !Object.values(PROPOSTA_SITUACAO).includes(normalized as PROPOSTA_SITUACAO)
  ) {
    return undefined;
  }
  return analysisQueueRules[normalized as PROPOSTA_SITUACAO];
};

export const isVisibleInAnalysisQueue = (
  statusCode: string | undefined
): boolean => getAnalysisQueueRule(statusCode)?.visibleInQueue === true;

export const getAnalysisQueueProposalOptions = (
  statusCode: string | undefined,
  proposalType?: ProposalTypeValue,
  featureFlags?: ProposalFeatureFlags,
  userPermissions?: ProposalActionPermission[]
): AnalysisQueueProposalMenuOption[] => {
  const rule = getAnalysisQueueRule(statusCode);
  if (!rule?.visibleInQueue) {
    return [];
  }

  return filterEnabledProposalActions(rule.actions, featureFlags)
    .filter(
      (action) =>
        !action.appliesTo?.length ||
        action.appliesTo.includes(proposalType as ProposalTypeEnum)
    )
    .filter((action) => {
      if (!action.permissions?.length) {
        return true;
      }

      return action.permissions.some((permission) =>
        userPermissions?.includes(permission)
      );
    })
    .map((action) => {
      const menuOption = { ...action };
      delete menuOption.appliesTo;
      return menuOption;
    });
};
